import { ChartOptions, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chroma from 'chroma-js';
import IconButton from 'components/IconButton';
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { CardBody, CardHeader, CardTitle, Col, Row } from 'reactstrap';

import { getHistoryChartData } from '../components/HistoryChart/helper';
import {
  LINE_CHART_OPTIONS,
  LINE_CHART_OPTIONS_NO_COUNT_LABELS,
} from '../components/HistoryChart/options';
import { TooltipLabelsType } from '../types';

import 'views/Admin/Dashboard/styles.css';

interface KPIHistoryChartProps {
  colors: string[];
  containerClassName?: string;
  datasets: number[][];
  headerActions?: React.ReactNode;
  isActive: boolean;
  labels: string[];
  newDataGenerationIndex: number;
  onExpandClick: () => void;
  showCounts: boolean;
  summaryValue: number | string;
  title: string;
  tooltipLabels: TooltipLabelsType;
  valueFormatter: (value: number) => string;
}

const getLineOptions = (
  showCounts: boolean,
  valueFormatter: (value: number) => string,
): ChartOptions<'line'> => {
  const options = showCounts
    ? LINE_CHART_OPTIONS
    : LINE_CHART_OPTIONS_NO_COUNT_LABELS;

  return {
    ...options,
    plugins: {
      ...options.plugins,
      datalabels: {
        ...options.plugins?.datalabels,
        formatter: (value) => valueFormatter(Number(value)),
      },
      tooltip: {
        ...options.plugins?.tooltip,
        callbacks: {
          ...options.plugins?.tooltip?.callbacks,
          label: (context: TooltipItem<'line'>) =>
            [context.dataset.label, valueFormatter(Number(context.raw || 0))]
              .filter(Boolean)
              .join(': '),
        },
      },
    },
  };
};

const KPIHistoryChart = ({
  colors,
  containerClassName,
  datasets,
  headerActions,
  isActive,
  labels,
  newDataGenerationIndex,
  onExpandClick,
  showCounts,
  summaryValue,
  title,
  tooltipLabels,
  valueFormatter,
}: KPIHistoryChartProps) => {
  const chartId = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const chartOptions = useMemo(
    () => getLineOptions(showCounts, valueFormatter),
    [showCounts, valueFormatter],
  );
  const renderChart = () => (
    <div
      className="chart-area"
      style={{
        cursor: 'pointer',
        height: '220px',
        width: '100%',
      }}
    >
      <Line
        options={chartOptions}
        data={getHistoryChartData(
          labels,
          tooltipLabels,
          datasets,
          colors,
          newDataGenerationIndex,
        )}
        plugins={[ChartDataLabels]}
      />
    </div>
  );

  return (
    <article
      className={`card-chart border border-light ${containerClassName || ''}`}
    >
      <CardHeader>
        <Row className="justify-content-between">
          <Col className="col-auto pr-0">
            <CardTitle tag="div" className="mb-0">
              <div className="d-flex flex-row align-items-baseline">
                <h1 className="d-inline mb-0">{summaryValue}</h1>
                <h5 className="card-category px-1">{title}</h5>
              </div>
            </CardTitle>
          </Col>
          <Col className="col p-0">
            <div className="col-12 d-flex flex-row-reverse">
              <IconButton
                id={`expand-kpi-history-${chartId}`}
                active={isActive}
                iconClass={
                  isActive
                    ? 'bi bi-arrows-angle-contract'
                    : 'bi bi-arrows-angle-expand'
                }
                size="sm"
                tooltip={isActive ? `Contract ${title}` : `Expand ${title}`}
                onClick={onExpandClick}
              />
              {headerActions}
            </div>
          </Col>
        </Row>
        <Row className="justify-content-between">
          <Col className="col-auto pr-0" />
          <Col className="col p-0">
            <div className="col-12 d-flex flex-row-reverse">
              <div className="mr-1">
                {tooltipLabels.map((tooltip, index) => {
                  const color = colors[index];
                  const backgroundColor = chroma(color).alpha(0.3);
                  return (
                    <div className="d-inline" key={tooltip.label}>
                      <div
                        className="m-1 p-1 legend-box"
                        style={{
                          borderColor: color,
                          backgroundColor,
                        }}
                      />
                      <span className="d-inline legend-text">
                        {tooltip.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>{renderChart()}</CardBody>
    </article>
  );
};

export default KPIHistoryChart;
